import { ChangeDetectorRef, Component, Input, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import {CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDragStart, transferArrayItem} from '@angular/cdk/drag-drop';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';

import {ModalService, TableComponent} from '@shared';

import {
    ActiveEmployee,
    ActiveEmployeeService,
    AddressFinderService,
    DragDropService,
    ProblemInstanceService,
    TaskService,
    TaskView
} from 'planner-shared';
import {AdminTaskModalComponent} from "../admin-task-modal/admin-task-modal.component";
import {EmployeeAdminTaskAddress} from "../../../shared/models/employee-admin-task-address.model";
import {FeatureFlags, FeatureFlagService} from "@core";


@UntilDestroy()
@Component({
    selector: 'app-employee-unassigned-tasks',
    templateUrl: './employee-unassigned-tasks.component.html',
    styleUrls: ['./employee-unassigned-tasks.component.scss'],
    standalone: false
})
export class EmployeeUnassignedTasksComponent implements OnInit {
    private readonly renderer = inject(Renderer2);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly taskService = inject(TaskService);
    private readonly dragDropService = inject(DragDropService);
    private readonly activeEmployeeService = inject(ActiveEmployeeService);
    private readonly modalService = inject(ModalService);
    private readonly addressFinderService = inject(AddressFinderService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly featureFlagService = inject(FeatureFlagService);

    @Input() public disableDrag: boolean;

    public loading: boolean;
    public reloading: boolean;
    public tasks: TaskView[];
    public rosterPresent: boolean;
    public isGeneralSkillUpdateFeatureEnabled: Observable<boolean>;
    private selectedEmployee: ActiveEmployee;

    private readonly InitSortBy = 'fromTime';
    private readonly InitSortBySecondary = 'toTime';

    @ViewChild(TableComponent, {static: true}) private readonly tableComponent: TableComponent;

    ngOnInit(): void {
        this.loading = true;

        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .subscribe(value => this.rosterPresent = value.valueOf());

        this.activeEmployeeService
            .getActiveEmployee()
            .pipe(untilDestroyed(this))
            .subscribe(employee => (this.selectedEmployee = employee));

        this.taskService
            .getReloadGeneralTasks()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                if (this.tasks.length > 0) {
                    this.reloading = true;
                    this.loadGeneralTasks();
                }
            });

        this.tableComponent.headerSortService
            .getHeaderSort()
            .pipe(untilDestroyed(this))
            .subscribe(sortValue => this.taskService.sortGeneralTasks(this.tasks, sortValue));

        this.loadGeneralTasks();

        this.isGeneralSkillUpdateFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.GeneralTaskSkillUpdate);
    }

    public drop(event: CdkDragDrop<TaskView[]>): void {
        if (event.previousContainer !== event.container) {
            this.disableDrag = true;

            const sameTask = event.container.data.find(task => task.originalTaskId === event.item.data.originalTaskId);
            if (sameTask) {
                sameTask.numberOfEmployees = sameTask.numberOfEmployees ? ++sameTask.numberOfEmployees : 2;
                event.previousContainer.data.splice(event.previousIndex, 1);
            } else {
                this.taskService.getGeneralTasks().subscribe(tasks => {
                    const task = tasks.find(i => i.originalTaskId === event.item.data.originalTaskId);
                    if (task) {
                        task.numberOfEmployees = 1;
                        event.previousContainer.data.splice(event.previousIndex, 1);
                        transferArrayItem([task], event.container.data, 0, event.currentIndex);
                    } else {
                        event.item.data.numberOfEmployees = 1;
                        transferArrayItem(
                            event.previousContainer.data,
                            event.container.data,
                            event.previousIndex,
                            event.currentIndex
                        );
                    }
                });
            }

            this.taskService
                .unassignGeneralTaskFromEmployee(
                    this.selectedEmployee.employeeId,
                    this.selectedEmployee.workHourId,
                    event.item.data.originalTaskId
                )
                .pipe(
                    catchError(error => {
                        if (sameTask) {
                            sameTask.numberOfEmployees--;
                            transferArrayItem([event.item.data], event.previousContainer.data, 0, event.previousIndex);
                        } else {
                            transferArrayItem(
                                event.container.data,
                                event.previousContainer.data,
                                event.currentIndex,
                                event.previousIndex
                            );
                        }

                        return throwError(error);
                    })
                )
                .subscribe(() => {
                    event.item.data.employeeId = null;
                    event.item.data.workHourId = null;
                    this.tableComponent.headerSortService.resort();
                    this.activeEmployeeService.resetActiveEmployeeTasks();
                    this.disableDrag = false;
                    this.loadGeneralTasks();
                });
        } else if (event.item.data.numberOfEmployees != null) {
            event.item.data.numberOfEmployees++;
        }
    }

    public exited(event: CdkDragExit<TaskView[], TaskView>): void {
        if (
            event.item.dropContainer.id !== event.container.id ||
            !event.item.data.numberOfEmployees ||
            event.item.data.numberOfEmployees <= 0
        ) {
            return;
        }

        if (
            !this.tableComponent.bodyContainerElement.nativeElement.firstElementChild.contains(
                event.item.element.nativeElement
            )
        ) {
            const placeholderElement = event.item.getPlaceholderElement();
            this.renderer.addClass(placeholderElement, 'cdk-drag-placeholder');
            this.renderer.removeStyle(event.item.element.nativeElement, 'display');
            this.renderer.insertBefore(
                this.tableComponent.bodyContainerElement.nativeElement.firstElementChild,
                event.item.element.nativeElement,
                placeholderElement
            );
        }
    }

    public entered(event: CdkDragEnter<TaskView[], TaskView>): void {
        const task = event.container.data.find(item => item.originalTaskId === event.item.data.originalTaskId);

        this.dragDropService.resetPlaceholderPositionInCurrentContainer(event);
        if (task && task.numberOfEmployees > 0) {
            this.dragDropService.hidePlaceholder(event);
        } else {
            this.dragDropService.showPlaceholder(event);
        }
    }

    public started(event: CdkDragStart<TaskView>): void {
        if (event.source.data.numberOfEmployees) {
            event.source.data.numberOfEmployees--;
            this.changeDetectorRef.detectChanges();
        }

        this.dragDropService.setDraggingTask(event.source.data);
    }

    public released(): void {
        this.dragDropService.setDraggingTask(null);
    }

    public openAdminTaskAddressModal(task: TaskView): void {
        this.modalService.openCustomModal(
            AdminTaskModalComponent,
            null,
            null,
            task,
            false
        );
    }

    public isTaskUnallocated(task: TaskView): boolean {
        return task.numberOfEmployees <= task.feasibilityInfo.numberOfNonAllocatedTasks;
    }

    public loadGeneralTasks(): void {
        this.taskService.getGeneralTasks(true).subscribe(tasks => {
            this.tasks = tasks;
            this.tableComponent.headerSortService.setHeaderSort(this.InitSortBy, true, this.InitSortBySecondary, true);
            this.loading = false;
            this.reloading = false;
            this.validateUnassignedTasks();
            this.changeDetectorRef.detectChanges();
        });
    }

    private validateUnassignedTasks(): void {
        this.loading = true;
        this.problemInstanceService.getProblemInstance().subscribe(instanceResult => {
            const adminTasksRequest: EmployeeAdminTaskAddress[] = new Array();
            this.tasks.filter(task => task.streetAddressNameNumberAndLetter).forEach(task => {
                const adminTask = new EmployeeAdminTaskAddress(task.taskId, task.streetAddressNameNumberAndLetter, task.zipCode);
                adminTasksRequest.push(adminTask);
            });
            this.tasks.filter(task => !task.streetAddressNameNumberAndLetter).forEach(task => task.addressIsValid = true);
            this.addressFinderService.validateAdminTaskAddresses(adminTasksRequest, instanceResult.problemInstance.officeInfo).subscribe(
                result => {
                    result.patientToAddressStatusList.forEach(addressStatus => {
                        const task = this.tasks.find(task => task.originalTaskId === addressStatus.patientId);
                        task.addressIsValid = addressStatus.status;
                    });
                    this.loading = false;
                });
        });

    }

    public editTask(task: TaskView, event: MouseEvent) {
        this.disableDrag = true;
        task.editing = true;
        event.stopPropagation();
    }
}
